import { formatFrequency, formatCategoryName } from '../../util/RecUtil';
import { isEntryLocked } from '../../util/protocolPermissions';

/**
 * Unified protocol entry display.
 * Shows entries as clean rows with optional edit mode for notes/instructions.
 *
 * When `effectiveLocked` is true, each doctor-authored entry renders a padlock
 * to the right of its name (lock/edit gating is by `author === 'doctor'`).
 */
function ProtocolEntryList({
    entries,
    editMode = false,
    doctorViewMode = false,
    effectiveLocked = false,
    lockedByName = null,
    patientNotes = '',
    doctorNotes = '',
    onPatientNotesChange,
    onDoctorNotesChange,
    onInstructionsChange,
}) {
    if (!entries || entries.length === 0) return null;

    return (
        <div className="protocol-entries">
            <div className="protocol-entries-list">
                {entries.map((entry, i) => (
                    <div key={`${entry.name}-${i}`} className="protocol-entry-row">
                        <span className="protocol-entry-rank">{i + 1}</span>
                        <div className="protocol-entry-details">
                            <div className="protocol-entry-name">
                                {entry.name}
                                {isEntryLocked(entry, { effectiveLocked }) && (
                                    <span
                                        className="protocol-entry-lock"
                                        title={doctorViewMode
                                            ? 'Locked for the patient — only you can change this'
                                            : (lockedByName
                                                ? `Locked by Dr. ${lockedByName} — only your doctor can change this`
                                                : 'Locked by your doctor — only your doctor can change this')}
                                    >
                                        <svg width="11" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                            <path d="M7 11V7a5 5 0 0110 0v4" />
                                        </svg>
                                    </span>
                                )}
                            </div>
                            <div className="protocol-entry-dose">
                                {entry.dose} {entry.unit} {formatFrequency(entry.frequency, entry.freqUnit)}
                                {entry.emptyStomach != null && <>
                                    <span className="protocol-row-sep">&middot;</span>
                                    <span className="protocol-row-timing">{entry.emptyStomach ? 'Empty stomach' : 'With food'}</span>
                                </>}
                            </div>
                            {editMode && doctorViewMode ? (
                                <textarea
                                    className="protocol-entry-instructions-input"
                                    placeholder="Add instructions..."
                                    value={entry.instructions || ''}
                                    onChange={(e) => onInstructionsChange?.(i, e.target.value)}
                                    rows={1}
                                />
                            ) : entry.instructions ? (
                                <div className="protocol-entry-instructions">{entry.instructions}</div>
                            ) : null}
                            {entry.category && (
                                <div className="protocol-row-cats">
                                    <span className="protocol-row-cat-tag">{formatCategoryName(entry.category)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Patient notes — patient edits, doctor sees read-only */}
            <div className="protocol-notes-section">
                <h3 className="protocol-notes-heading">Notes</h3>
                {editMode && !doctorViewMode ? (
                    <textarea
                        className="protocol-notes-textarea"
                        placeholder="Add notes about your protocol..."
                        value={patientNotes}
                        onChange={(e) => onPatientNotesChange?.(e.target.value)}
                        rows={3}
                    />
                ) : patientNotes ? (
                    <p className="protocol-notes-text">{patientNotes}</p>
                ) : (
                    <p className="protocol-notes-empty">No notes yet.</p>
                )}
            </div>

            {/* Doctor notes — doctor edits, hidden from patient */}
            {doctorViewMode && (
                <div className="protocol-notes-section protocol-doctor-notes">
                    <h3 className="protocol-notes-heading">Doctor Notes</h3>
                    {editMode ? (
                        <textarea
                            className="protocol-notes-textarea"
                            placeholder="Private notes (not visible to patient)..."
                            value={doctorNotes}
                            onChange={(e) => onDoctorNotesChange?.(e.target.value)}
                            rows={3}
                        />
                    ) : doctorNotes ? (
                        <p className="protocol-notes-text">{doctorNotes}</p>
                    ) : (
                        <p className="protocol-notes-empty">No doctor notes yet.</p>
                    )}
                </div>
            )}
        </div>
    );
}

export default ProtocolEntryList;
